import React, { useEffect, useState } from 'react';
import {
  Card, CardBody, CardHeader, Heading, SimpleGrid, FormControl, FormLabel,
  FormHelperText, Input, InputGroup, InputRightElement, Select, Button, HStack,
  VStack, Text, Box, Badge, Switch, Alert, AlertIcon, AlertTitle,
  AlertDescription, Divider, List, ListItem, ListIcon, useToast, Textarea,
  Tag, Wrap, WrapItem, IconButton, Code,
} from '@chakra-ui/react';
import { CheckCircleIcon, InfoIcon, ViewIcon, ViewOffIcon, AddIcon, CloseIcon } from '@chakra-ui/icons';
import { Trans, useTranslation } from 'react-i18next';
import PageShell from '../components/PageShell';
import emailSettingsService, { MASKED_PASSWORD } from '../services/emailSettingsService';

const EmailSettings = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [newNotificationEmail, setNewNotificationEmail] = useState('');
  const toast = useToast();

  const load = async () => {
    try {
      setLoading(true);
      const res = await emailSettingsService.get();
      setData(res);
      setForm({
        smtpHost: res.smtpHost || '',
        smtpPort: res.smtpPort || 587,
        smtpUsername: res.smtpUsername || '',
        // The server never sends the password back. Seed the field with the
        // mask so the operator sees "something is stored" and can save other
        // fields without retyping it.
        smtpPassword: res.smtpPasswordSet ? MASKED_PASSWORD : '',
        smtpUseTls: res.smtpUseTls,
        smtpUseSsl: res.smtpUseSsl,
        fromName: res.fromName || '',
        fromAddress: res.fromAddress || '',
        emailFooter: res.emailFooter || '',
        sendBookingNotifications: res.sendBookingNotifications,
        notificationEmails: res.notificationEmails || [],
      });
      setError('');
    } catch {
      setError(t('emailSettings.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setF = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  const setBool = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.checked }));

  const applyPreset = (presetId) => {
    const preset = (data?.providerPresets || []).find((p) => p.id === presetId);
    if (!preset) return;
    setForm((prev) => ({
      ...prev,
      smtpHost: preset.host || prev.smtpHost,
      smtpPort: preset.port,
      smtpUseTls: preset.useTls,
      smtpUseSsl: preset.useSsl,
    }));
    if (preset.note) {
      toast({ title: preset.label, description: preset.note, status: 'info', duration: 7000 });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, smtpPort: Number(form.smtpPort) };
      // Never round-trip the mask as a real password.
      if (payload.smtpPassword === MASKED_PASSWORD) delete payload.smtpPassword;
      const res = await emailSettingsService.update(payload);
      setData(res);
      toast({ title: t('emailSettings.toasts.saved'), status: 'success', duration: 2500 });
      load();
    } catch (err) {
      const body = err.response?.data;
      const detail = body?.errors
        ? Object.values(body.errors).join(' ')
        : body?.detail || t('emailSettings.toasts.checkFields');
      toast({ title: t('emailSettings.toasts.saveFailed'), description: detail,
              status: 'error', duration: 9000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const payload = {
        smtpHost: form.smtpHost,
        smtpPort: Number(form.smtpPort),
        smtpUsername: form.smtpUsername,
        smtpUseTls: form.smtpUseTls,
        smtpUseSsl: form.smtpUseSsl,
        fromName: form.fromName,
        fromAddress: form.fromAddress,
      };
      if (form.smtpPassword && form.smtpPassword !== MASKED_PASSWORD) {
        payload.smtpPassword = form.smtpPassword;
      }
      if (testEmail.trim()) payload.testEmail = testEmail.trim();

      const res = await emailSettingsService.test(payload);
      toast({ title: t('emailSettings.toasts.connectionOk'), description: res.message,
              status: 'success', duration: 6000 });
    } catch (err) {
      const body = err.response?.data;
      toast({
        title: body?.detail || t('emailSettings.toasts.connectionFailed'),
        description: body?.errors?.smtp || '',
        status: 'error', duration: 12000, isClosable: true,
      });
    } finally {
      setTesting(false);
    }
  };

  const addNotificationEmail = () => {
    const value = newNotificationEmail.trim();
    if (!value) return;
    setForm((prev) => ({
      ...prev,
      notificationEmails: prev.notificationEmails.includes(value)
        ? prev.notificationEmails
        : [...prev.notificationEmails, value],
    }));
    setNewNotificationEmail('');
  };

  return (
    <PageShell
      title={t('emailSettings.page.title')}
      subtitle={t('emailSettings.page.subtitle')}
      breadcrumbs={[{ label: t('emailSettings.page.title') }]}
      loading={loading} error={error}
      actions={
        <Button size="sm" colorScheme="blue" onClick={handleSave} isLoading={saving}>
          {t('emailSettings.buttons.saveSettings')}
        </Button>
      }
    >
      {form && data && (
        <VStack spacing={6} align="stretch">
          {/* Current state */}
          {data.configured ? (
            <Alert status="success" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="sm">{t('emailSettings.status.ownServerTitle')}</AlertTitle>
                <AlertDescription fontSize="sm">
                  <Trans
                    i18nKey="emailSettings.status.ownServerBody"
                    values={{ host: data.smtpHost, port: data.smtpPort, from: data.effectiveFrom }}
                    components={{ code: <Code fontSize="xs" /> }}
                  />
                </AlertDescription>
              </Box>
            </Alert>
          ) : (
            <Alert status={data.canSendEmail ? 'info' : 'warning'} borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="sm">
                  {data.canSendEmail
                    ? t('emailSettings.status.sharedTitle')
                    : t('emailSettings.status.noServerTitle')}
                </AlertTitle>
                <AlertDescription fontSize="sm">
                  {data.canSendEmail
                    ? t('emailSettings.status.sharedBody')
                    : t('emailSettings.status.noServerBody')}
                </AlertDescription>
              </Box>
            </Alert>
          )}

          {/* What this governs */}
          <Card shadow="sm" borderWidth="1px">
            <CardHeader pb={2}>
              <Heading size="sm">{t('emailSettings.scope.title')}</Heading>
            </CardHeader>
            <CardBody pt={0}>
              <Text fontSize="sm" color="gray.600" mb={3}>
                {t('emailSettings.scope.intro')}
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={1}>
                <List spacing={1}>
                  {(data.appliesTo || []).map((item) => (
                    <ListItem key={item} fontSize="sm">
                      <ListIcon as={CheckCircleIcon} color="green.400" />
                      {item}
                    </ListItem>
                  ))}
                </List>
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* SMTP credentials */}
          <Card shadow="sm" borderWidth="1px">
            <CardHeader pb={2}>
              <HStack justify="space-between">
                <Heading size="sm">{t('emailSettings.smtp.title')}</Heading>
                {data.smtpPasswordSet && (
                  <Badge colorScheme="green" fontSize="2xs">{t('emailSettings.smtp.passwordSaved')}</Badge>
                )}
              </HStack>
            </CardHeader>
            <CardBody pt={0}>
              <VStack align="stretch" spacing={4}>
                <FormControl maxW="360px">
                  <FormLabel fontSize="sm">{t('emailSettings.smtp.quickSetup')}</FormLabel>
                  <Select size="sm" defaultValue=""
                    onChange={(e) => { applyPreset(e.target.value); e.target.value = ''; }}>
                    <option value="">{t('emailSettings.smtp.chooseProvider')}</option>
                    {(data.providerPresets || []).map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </Select>
                  <FormHelperText fontSize="xs">
                    {t('emailSettings.smtp.quickSetupHelp')}
                  </FormHelperText>
                </FormControl>

                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                  <FormControl gridColumn={{ lg: 'span 2' }}>
                    <FormLabel fontSize="sm">{t('emailSettings.smtp.host')}</FormLabel>
                    <Input size="sm" value={form.smtpHost} onChange={setF('smtpHost')}
                      placeholder="smtp.yourprovider.com" />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">{t('emailSettings.smtp.port')}</FormLabel>
                    <Input size="sm" type="number" value={form.smtpPort}
                      onChange={setF('smtpPort')} />
                    <FormHelperText fontSize="xs">587 = TLS, 465 = SSL</FormHelperText>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">{t('emailSettings.smtp.encryption')}</FormLabel>
                    <Select size="sm"
                      value={form.smtpUseSsl ? 'ssl' : (form.smtpUseTls ? 'tls' : 'none')}
                      onChange={(e) => setForm((prev) => ({
                        ...prev,
                        smtpUseTls: e.target.value === 'tls',
                        smtpUseSsl: e.target.value === 'ssl',
                      }))}>
                      <option value="tls">STARTTLS</option>
                      <option value="ssl">SSL/TLS</option>
                      <option value="none">{t('emailSettings.smtp.encryptionNone')}</option>
                    </Select>
                  </FormControl>
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel fontSize="sm">{t('emailSettings.smtp.username')}</FormLabel>
                    <Input size="sm" value={form.smtpUsername} onChange={setF('smtpUsername')}
                      placeholder={t('emailSettings.smtp.usernamePlaceholder')} autoComplete="off" />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">{t('emailSettings.smtp.password')}</FormLabel>
                    <InputGroup size="sm">
                      <Input type={showPassword ? 'text' : 'password'}
                        value={form.smtpPassword}
                        onChange={setF('smtpPassword')}
                        onFocus={(e) => {
                          // Clear the mask on first focus so typing replaces it
                          // rather than appending to a fake value.
                          if (e.target.value === MASKED_PASSWORD) {
                            setForm((prev) => ({ ...prev, smtpPassword: '' }));
                          }
                        }}
                        autoComplete="new-password" />
                      <InputRightElement>
                        <IconButton size="xs" variant="ghost"
                          aria-label={showPassword ? t('emailSettings.smtp.hidePassword') : t('emailSettings.smtp.showPassword')}
                          icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                          onClick={() => setShowPassword((v) => !v)} />
                      </InputRightElement>
                    </InputGroup>
                    <FormHelperText fontSize="xs">
                      {t('emailSettings.smtp.appPasswordHint')}
                    </FormHelperText>
                  </FormControl>
                </SimpleGrid>

                <Alert status="info" borderRadius="md" fontSize="xs">
                  <AlertIcon />
                  {t('emailSettings.smtp.blankFieldsHint')}
                </Alert>
              </VStack>
            </CardBody>
          </Card>

          {/* Sender identity */}
          <Card shadow="sm" borderWidth="1px">
            <CardHeader pb={2}><Heading size="sm">{t('emailSettings.sender.title')}</Heading></CardHeader>
            <CardBody pt={0}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">{t('emailSettings.sender.fromName')}</FormLabel>
                  <Input size="sm" value={form.fromName} onChange={setF('fromName')}
                    placeholder={data.hotelEmail ? t('emailSettings.sender.fromNamePlaceholder') : ''} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('emailSettings.sender.fromAddress')}</FormLabel>
                  <Input size="sm" type="email" value={form.fromAddress}
                    onChange={setF('fromAddress')}
                    placeholder={data.hotelEmail || 'stay@yourhotel.com'} />
                  <FormHelperText fontSize="xs">
                    {t('emailSettings.sender.fromAddressHelp')}
                  </FormHelperText>
                </FormControl>
              </SimpleGrid>

              <FormControl mt={4}>
                <FormLabel fontSize="sm">{t('emailSettings.sender.footer')}</FormLabel>
                <Textarea size="sm" rows={3} value={form.emailFooter}
                  onChange={setF('emailFooter')}
                  placeholder={t('emailSettings.sender.footerPlaceholder')} />
              </FormControl>
            </CardBody>
          </Card>

          {/* Staff notifications */}
          <Card shadow="sm" borderWidth="1px">
            <CardHeader pb={2}><Heading size="sm">{t('emailSettings.notifications.title')}</Heading></CardHeader>
            <CardBody pt={0}>
              <VStack align="stretch" spacing={4}>
                <FormControl display="flex" alignItems="center">
                  <Switch id="notif" size="sm" mr={3}
                    isChecked={form.sendBookingNotifications}
                    onChange={setBool('sendBookingNotifications')} />
                  <FormLabel htmlFor="notif" fontSize="sm" mb={0}>
                    {t('emailSettings.notifications.enable')}
                  </FormLabel>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">{t('emailSettings.notifications.recipients')}</FormLabel>
                  <HStack maxW="480px">
                    <Input size="sm" type="email" value={newNotificationEmail}
                      placeholder="frontdesk@yourhotel.com"
                      onChange={(e) => setNewNotificationEmail(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNotificationEmail(); } }} />
                    <Button size="sm" leftIcon={<AddIcon />} onClick={addNotificationEmail}>
                      {t('emailSettings.notifications.add')}
                    </Button>
                  </HStack>
                  <Wrap spacing={2} mt={2}>
                    {form.notificationEmails.map((addr) => (
                      <WrapItem key={addr}>
                        <Tag size="sm" colorScheme="blue">
                          {addr}
                          <IconButton size="2xs" ml={1} variant="ghost" aria-label={t('emailSettings.notifications.remove')}
                            icon={<CloseIcon boxSize={2} />}
                            onClick={() => setForm((prev) => ({
                              ...prev,
                              notificationEmails: prev.notificationEmails.filter((a) => a !== addr),
                            }))} />
                        </Tag>
                      </WrapItem>
                    ))}
                    {form.notificationEmails.length === 0 && (
                      <Text fontSize="xs" color="gray.400">
                        {t('emailSettings.notifications.fallback', {
                          email: data.hotelEmail || t('emailSettings.notifications.notSet'),
                        })}
                      </Text>
                    )}
                  </Wrap>
                </FormControl>
              </VStack>
            </CardBody>
          </Card>

          {/* Test */}
          <Card shadow="sm" borderWidth="1px">
            <CardHeader pb={2}><Heading size="sm">{t('emailSettings.test.title')}</Heading></CardHeader>
            <CardBody pt={0}>
              <Text fontSize="sm" color="gray.600" mb={3}>
                {t('emailSettings.test.intro')}
              </Text>
              <HStack maxW="560px">
                <Input size="sm" type="email" value={testEmail}
                  placeholder={t('emailSettings.test.placeholder')}
                  onChange={(e) => setTestEmail(e.target.value)} />
                <Button size="sm" onClick={handleTest} isLoading={testing}
                  leftIcon={<InfoIcon />}>
                  {testEmail.trim() ? t('emailSettings.test.testAndSend') : t('emailSettings.test.testConnection')}
                </Button>
              </HStack>
            </CardBody>
          </Card>

          <Divider />
          <HStack justify="flex-end">
            <Button colorScheme="blue" onClick={handleSave} isLoading={saving}>
              {t('emailSettings.buttons.saveSettings')}
            </Button>
          </HStack>
        </VStack>
      )}
    </PageShell>
  );
};

export default EmailSettings;
